import * as React from 'react';
import { PieChart, Pie, Cell } from 'recharts';
import splitDonutTooltip from './Tooltip';
import { Typography, styled, useTheme } from '@mui/material';
import Column from '../../../../components/Column';
import Row from '../../../../components/Row';

export interface SplitDonutData {
  value: number;
  label: string;
  color: string;
  opacity?: number;
}

export interface SplitDonutProps {
  size: number;
  type: 'pieChart';
  thickness: number;
  data: SplitDonutData[];
  showLegend?: boolean;
  tooltipValueFormatter?: (n: number) => string | number;
}

const SplitDonut: React.FC<SplitDonutProps> = ({ data, size, thickness, tooltipValueFormatter, showLegend }) => {
  const [activeIndex, setActiveIndex] = React.useState(0);
  const activeShape = data[activeIndex];
  const theme = useTheme();
  return (
    <PieWrapper>
      <Tooltip style={{ alignItems: 'center' }}>
        <TooltipText variant="body2">{activeShape?.label}</TooltipText>
        <TooltipText variant="body2" color="textSecondary">
          {tooltipValueFormatter !== undefined ? tooltipValueFormatter(activeShape ? activeShape.value : 0) : activeShape?.value}
        </TooltipText>
      </Tooltip>

      <PieChart width={size} height={size}>
        <Pie
          data={data}
          innerRadius={size / 2 - thickness}
          outerRadius={size / 2}
          dataKey="value"
          blendStroke={true}
          startAngle={90}
          endAngle={-270}
          activeIndex={activeIndex}
          activeShape={splitDonutTooltip}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onMouseEnter={(_, index: number) => {
            setActiveIndex(index);
          }}
          minAngle={0.01}
        >
          {data.map(({ color, label }) => {
            return <Cell key={`${label}-${color}`} fill={color} />;
          })}
        </Pie>
      </PieChart>
      <>
        {showLegend && (
          <Legend centered={true}>
            <>
              {data?.map((d, i) => (
                <Column key={`${d.label}-${i}`} style={{ margin: `0 ${theme.spacing(1)}` }}>
                  <Row vAlign="center">
                    <Dot style={{ backgroundColor: d.color, opacity: d.opacity }} />
                    <Typography variant="body2">{d.label}</Typography>
                  </Row>
                </Column>
              ))}
            </>
          </Legend>
        )}
      </>
    </PieWrapper>
  );
};

export default SplitDonut;

const Tooltip = styled(Column)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
});

const TooltipText = styled(Typography)({
  fontSize: '12px',
});
const Legend = styled(Row)(({ theme }) => ({
  marginTop: theme.spacing(3),
  flexWrap: 'wrap',
}));

const Dot = styled(Row)(({ theme }) => ({
  height: 6,
  width: 6,
  borderRadius: '50%',
  marginRight: theme.spacing(2),
}));

const PieWrapper = styled(Column)(() => ({
  position: 'relative',
  alignItems: 'center',
  minHeight: 300,
  justifyContent: 'center',
}));
